import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Loader2, X, Sparkles, CheckCircle2, Trash2, FileSpreadsheet,
  ChevronRight, Wand2, DollarSign, Ban, RotateCcw, LineChart,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { claimTitle } from "@/lib/utils";

const Mono = ({ children, className = "", ...rest }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`} {...rest}>{children}</span>
);
const inp = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500";
const lbl = "font-mono uppercase tracking-[0.2em] text-[0.55rem] text-white/45 block mb-1";
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const MEASURE_STYLES = {
  EW: "border-amber-400/40 text-amber-300", IRW: "border-sky-400/40 text-sky-300",
  EPAR: "border-gold-500/40 text-gold-300", CDO: "border-purple-400/40 text-purple-300",
  unclassified: "border-white/15 text-white/40",
};

export default function LedgerPage() {
  const [data, setData] = useState(null);
  const [batchFilter, setBatchFilter] = useState("");
  const [measureFilter, setMeasureFilter] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [claims, setClaims] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assetsByClaim, setAssetsByClaim] = useState({});
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const nav = useNavigate();

  const load = async () => {
    const params = new URLSearchParams();
    if (batchFilter) params.set("batch_id", batchFilter);
    if (measureFilter) params.set("measure", measureFilter);
    const { data: d } = await api.get(`/fieldclaim/ledger?${params}`);
    setData(d);
  };
  useEffect(() => { load(); }, [batchFilter, measureFilter]); // eslint-disable-line
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/fieldclaim/claims").then(async (r) => {
      setClaims(r.data);
      const map = {};
      await Promise.all(r.data.map(async (c) => {
        try { map[c.id] = (await api.get(`/fieldclaim/claims/${c.id}/assets`)).data; } catch { map[c.id] = []; }
      }));
      setAssetsByClaim(map);
    }).catch(() => {});
  }, []);

  const act = async (label, fn) => {
    setBusy(label); setNotice("");
    try { const msg = await fn(); if (msg) setNotice(msg); await load(); }
    catch (e) { setNotice(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed."); }
    finally { setBusy(""); }
  };

  const activeBatch = data?.batches.find((b) => b.id === batchFilter);

  return (
    <div className="space-y-8 text-white" data-testid="ledger-page">
      <button onClick={() => nav("/dashboard/fieldclaim")} data-testid="ledger-back" className="inline-flex items-center gap-2 text-white/50 hover:text-gold-400 transition-colors text-sm">
        <ArrowLeft className="h-4 w-4" /> FieldClaim
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Financial register · may span multiple events</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Ledger Importer</h1>
          <p className="text-white/55 mt-3 font-light max-w-2xl">
            Upload your finance-system export — the mapping wizard remembers each council format. Lines are cleaned,
            AI-classified to the right event and asset, separated into EW / IRW / EPAR / CDO, then pushed into claim
            cost items and rolled into Portfolio project spend.
          </p>
        </div>
        <button onClick={() => setWizardOpen(true)} data-testid="ledger-upload-btn" className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2">
          <Upload className="h-4 w-4" /> Upload ledger
        </button>
      </div>

      {/* measure separation strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ledger-measure-totals">
          {["EW", "IRW", "EPAR", "CDO", "unclassified"].map((mm) => (
            <button key={mm} onClick={() => setMeasureFilter(measureFilter === mm ? "" : mm)}
              data-testid={`measure-total-${mm}`}
              className={`border px-4 py-3 text-left transition-colors ${MEASURE_STYLES[mm]} ${measureFilter === mm ? "bg-gold-500/10" : "bg-navy-900/40 hover:bg-navy-800"}`}>
              <Mono className="text-[0.5rem] opacity-80">{mm === "unclassified" ? "Unclassified" : mm}</Mono>
              <div className="font-mono text-lg mt-0.5">{money(data.measure_totals[mm]?.amount)}</div>
              <div className="text-[0.6rem] text-white/35 font-light">{data.measure_totals[mm]?.count || 0} lines</div>
            </button>
          ))}
        </div>
      )}

      {/* batches */}
      {data && data.batches.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ledger-batches">
          <button onClick={() => setBatchFilter("")} className={`border px-3 py-1.5 text-xs ${!batchFilter ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/50"}`}>All batches</button>
          {data.batches.map((b) => (
            <button key={b.id} onClick={() => setBatchFilter(batchFilter === b.id ? "" : b.id)} data-testid={`batch-chip-${b.id}`}
              className={`border px-3 py-1.5 text-xs inline-flex items-center gap-2 ${batchFilter === b.id ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/60 hover:text-gold-300"}`}>
              <FileSpreadsheet className="h-3 w-3" /> {b.filename} · {b.clean_rows} lines · {money(b.total_amount)}
            </button>
          ))}
        </div>
      )}

      {/* batch actions */}
      {activeBatch && (
        <div className="border border-gold-500/25 bg-navy-900/40 p-4 flex items-center gap-2 flex-wrap" data-testid="ledger-batch-actions">
          <button onClick={() => act("classify", async () => { const { data: r } = await api.post(`/fieldclaim/ledger/batches/${activeBatch.id}/classify`); return `AI classified ${r.classified} line(s).`; })}
            disabled={!!busy} data-testid="ledger-classify-btn"
            className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 font-semibold px-4 py-2 text-sm hover:bg-gold-400 disabled:opacity-50">
            {busy === "classify" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI classify (event · asset · EW/IRW)
          </button>
          <button onClick={() => act("accept", async () => { const { data: r } = await api.post(`/fieldclaim/ledger/batches/${activeBatch.id}/accept-suggestions`, { min_confidence: 0.6 }); return `Accepted ${r.accepted} suggestion(s) at ≥60% confidence.`; })}
            disabled={!!busy} data-testid="ledger-accept-btn"
            className="inline-flex items-center gap-2 border border-emerald-400/50 text-emerald-300 px-4 py-2 text-sm hover:bg-emerald-400/10 disabled:opacity-50">
            <Wand2 className="h-4 w-4" /> Accept suggestions
          </button>
          <button onClick={() => act("push", async () => { const { data: r } = await api.post(`/fieldclaim/ledger/batches/${activeBatch.id}/push-to-costs`); return `Pushed ${r.created} line(s) — ${money(r.total)} into cost items.`; })}
            disabled={!!busy} data-testid="ledger-push-btn"
            className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-sm hover:bg-gold-500/10 disabled:opacity-50">
            {busy === "push" ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />} Push matched → cost items
          </button>
          <button onClick={() => act("pushproj", async () => {
              const { data: r } = await api.post(`/fieldclaim/ledger/batches/${activeBatch.id}/push-to-project-spend`);
              return r.pushed_lines
                ? `Rolled ${r.pushed_lines} line(s) — ${money(r.total)} into Portfolio project spend.`
                : "No lines matched to a project yet — set a Project on lines first.";
            })}
            disabled={!!busy} data-testid="ledger-push-project-btn"
            className="inline-flex items-center gap-2 border border-sky-400/50 text-sky-300 px-4 py-2 text-sm hover:bg-sky-400/10 disabled:opacity-50">
            {busy === "pushproj" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LineChart className="h-4 w-4" />} Push matched → Portfolio spend
          </button>
          <button onClick={() => { if (window.confirm("Delete this batch? Unpushed lines will be removed.")) act("del", async () => { await api.delete(`/fieldclaim/ledger/batches/${activeBatch.id}`); setBatchFilter(""); return "Batch deleted."; }); }}
            disabled={!!busy} data-testid="ledger-delete-batch-btn"
            className="ml-auto border border-red-500/30 text-red-300/70 hover:text-red-300 p-2 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
        </div>
      )}

      {notice && <div className="text-gold-300 text-sm" data-testid="ledger-notice">{notice}</div>}

      {/* lines */}
      {data === null ? (
        <div className="text-white/40 text-sm">Loading ledger…</div>
      ) : data.lines.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45 font-light" data-testid="ledger-empty">
          No ledger lines{measureFilter ? ` for ${measureFilter}` : ""} yet. Upload a finance export to begin.
        </div>
      ) : (
        <LinesTable lines={data.lines} claims={claims} assetsByClaim={assetsByClaim} projects={projects} onChanged={load} />
      )}

      {wizardOpen && <LedgerWizard onClose={() => setWizardOpen(false)} onDone={(batchId) => { setWizardOpen(false); setBatchFilter(batchId); load(); }} />}
    </div>
  );
}

function LinesTable({ lines, claims, assetsByClaim, projects, onChanged }) {
  const allAssets = useMemo(() => {
    const out = [];
    claims.forEach((c) => (assetsByClaim[c.id] || []).forEach((a) => out.push({ ...a, claim_name: claimTitle(c) })));
    return out;
  }, [claims, assetsByClaim]);

  const patch = async (line, body) => { await api.patch(`/fieldclaim/ledger/lines/${line.id}`, body); onChanged(); };

  return (
    <div className="border border-gold-500/25 bg-navy-900/40 overflow-x-auto" data-testid="ledger-lines">
      <table className="w-full text-sm min-w-[1180px]">
        <thead><tr className="text-left border-b border-gold-500/15 bg-navy-950/60">
          {["Date", "Description", "WO / Ref", "Amount", "Measure", "Event · Asset", "Portfolio project", "AI suggestion", ""].map((h, i) => (
            <th key={i} className="py-2.5 px-3 font-mono uppercase tracking-widest text-[0.55rem] text-white/40">{h}</th>))}
        </tr></thead>
        <tbody>
          {lines.slice(0, 300).map((l) => (
            <tr key={l.id} className={`border-b border-white/5 ${l.excluded ? "opacity-35" : ""} ${l.pushed_cost_item_id ? "bg-emerald-400/5" : ""}`} data-testid={`ledger-line-${l.id}`}>
              <td className="py-2 px-3 font-mono text-xs text-white/60 whitespace-nowrap">{l.date}</td>
              <td className="py-2 px-3 text-white/80 font-light max-w-[260px]"><div className="truncate" title={l.description}>{l.description}</div>
                {l.supplier && <div className="text-white/35 text-[0.65rem] truncate">{l.supplier}{l.invoice_no ? ` · ${l.invoice_no}` : ""}</div>}</td>
              <td className="py-2 px-3 font-mono text-xs text-white/45">{l.work_order || "—"}</td>
              <td className={`py-2 px-3 font-mono whitespace-nowrap ${l.amount < 0 ? "text-emerald-300" : "text-gold-300"}`}>{money(l.amount)}</td>
              <td className="py-2 px-3">
                <select value={l.measure || ""} onChange={(e) => patch(l, { measure: e.target.value || null })} data-testid={`line-measure-${l.id}`}
                  className={`bg-navy-950 border text-xs px-2 py-1 focus:outline-none ${MEASURE_STYLES[l.measure || "unclassified"]}`}>
                  <option value="">—</option>
                  {["EW", "IRW", "EPAR", "CDO"].map((mm) => <option key={mm} value={mm}>{mm}</option>)}
                </select>
              </td>
              <td className="py-2 px-3 min-w-[210px]">
                <select value={l.matched_asset_id || ""} onChange={(e) => e.target.value ? patch(l, { matched_asset_id: e.target.value }) : patch(l, { clear_match: true })}
                  data-testid={`line-asset-${l.id}`} className={inp + " text-xs py-1"}>
                  <option value="">— unmatched —</option>
                  {allAssets.map((a) => <option key={a.id} value={a.id}>{`${a.claim_name} · ${a.name}`}</option>)}
                </select>
              </td>
              <td className="py-2 px-3 min-w-[180px]">
                {l.project_pushed ? (
                  <span className="inline-flex items-center gap-1 text-sky-300 text-[0.6rem] font-mono uppercase" data-testid={`line-project-pushed-${l.id}`}>
                    <CheckCircle2 className="h-3 w-3" /> {l.matched_project_name || "in portfolio"}
                  </span>
                ) : (
                  <select value={l.matched_project_id || ""} onChange={(e) => e.target.value ? patch(l, { matched_project_id: e.target.value }) : patch(l, { clear_project: true })}
                    data-testid={`line-project-${l.id}`} className={inp + " text-xs py-1"}>
                    <option value="">— no project —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </td>
              <td className="py-2 px-3 max-w-[200px]">
                {l.ai_suggestion ? (
                  <button onClick={() => l.ai_suggestion.asset_id && patch(l, { matched_asset_id: l.ai_suggestion.asset_id, measure: l.measure || l.ai_suggestion.funding_measure })}
                    data-testid={`line-suggestion-${l.id}`} title={l.ai_suggestion.rationale}
                    className="text-left text-[0.65rem] leading-snug text-white/55 hover:text-gold-300">
                    <span className="text-gold-300/90">{Math.round((l.ai_suggestion.confidence || 0) * 100)}%</span>
                    {" "}{l.ai_suggestion.funding_measure || "?"} · {l.ai_suggestion.asset_name || l.ai_suggestion.claim_name || "no match"}
                  </button>
                ) : <span className="text-white/25 text-[0.65rem]">—</span>}
              </td>
              <td className="py-2 px-3 whitespace-nowrap text-right">
                {l.pushed_cost_item_id ? (
                  <span className="inline-flex items-center gap-1 text-emerald-300 text-[0.6rem] font-mono uppercase" data-testid={`line-pushed-${l.id}`}><CheckCircle2 className="h-3 w-3" /> pushed</span>
                ) : (
                  <button onClick={() => patch(l, { excluded: !l.excluded })} data-testid={`line-exclude-${l.id}`} title={l.excluded ? "Restore line" : "Exclude line"}
                    className="text-white/30 hover:text-red-300">{l.excluded ? <RotateCcw className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {lines.length > 300 && <div className="text-white/35 text-xs p-3">Showing first 300 of {lines.length} lines — use batch/measure filters to narrow down.</div>}
    </div>
  );
}

const TARGETS = [
  ["date", "Date *"], ["description", "Description *"], ["amount", "Amount"], ["debit", "Debit"], ["credit", "Credit"],
  ["account_code", "Account code"], ["work_order", "Work order / job"], ["supplier", "Supplier"], ["invoice_no", "Invoice / ref"], ["gst", "GST"],
];

function LedgerWizard({ onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [inspect, setInspect] = useState(null);
  const [mapping, setMapping] = useState({});
  const [amountMode, setAmountMode] = useState("single");
  const [saveTemplate, setSaveTemplate] = useState(true);
  const [templateName, setTemplateName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const upload = async (file) => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fieldclaim/ledger/inspect", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInspect(data);
      setMapping(data.suggested_mapping || {});
      setAmountMode(data.suggested_amount_mode || "single");
      if (data.matched_template) setTemplateName(data.matched_template.name);
      setStep(1);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not read that file. Use CSV or XLSX."); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/fieldclaim/ledger/import", {
        staging_id: inspect.staging_id, mapping, amount_mode: amountMode,
        save_template: saveTemplate && !!templateName, template_name: templateName || null,
      });
      setResult(data);
      setStep(2);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="ledger-wizard" className="bg-navy-900 border border-gold-500/30 w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 space-y-5 text-white">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl">Ledger setup wizard</h2>
          <button onClick={onClose} data-testid="ledger-wizard-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div className="flex items-center gap-1">
          {["Upload", "Map columns", "Import"].map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i < step ? "bg-emerald-400" : i === step ? "bg-gold-500" : "bg-navy-800"}`} />
              <Mono className={`text-[0.42rem] mt-1 block text-center ${i === step ? "text-gold-300" : "text-white/30"}`}>{i + 1}. {s}</Mono>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="lw-step-upload">
            <p className="text-white/55 text-sm font-light">
              Upload the finance-system export (CSV or XLSX). It can span <span className="text-gold-300">multiple disaster events</span> —
              you'll separate EW / IRW works after import. Column layouts are remembered per format.
            </p>
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="lw-file-btn"
              className="w-full border-2 border-dashed border-gold-500/30 hover:border-gold-500/60 py-12 flex flex-col items-center gap-3 transition-colors">
              {busy ? <Loader2 className="h-8 w-8 animate-spin text-gold-400" /> : <FileSpreadsheet className="h-8 w-8 text-gold-400" />}
              <span className="text-white/60 text-sm font-light">{busy ? "Reading file…" : "Click to choose the ledger file (CSV / XLSX)"}</span>
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" data-testid="lw-file-input"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          </div>
        )}

        {step === 1 && inspect && (
          <div className="space-y-4" data-testid="lw-step-map">
            {inspect.matched_template && (
              <div className="border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-sm px-3 py-2.5 flex items-center gap-2" data-testid="lw-template-matched">
                <CheckCircle2 className="h-4 w-4" /> Recognised format — applied saved template “{inspect.matched_template.name}”.
              </div>
            )}
            <div className="text-white/50 text-xs font-light">{inspect.filename} · {inspect.row_count} rows · {inspect.headers.length} columns detected</div>
            <div className="flex items-center gap-2">
              <Mono className="text-[0.5rem] text-white/45">Amount format:</Mono>
              {[["single", "Single amount column"], ["debit_credit", "Debit / Credit columns"]].map(([v, t]) => (
                <button key={v} onClick={() => setAmountMode(v)} data-testid={`lw-mode-${v}`}
                  className={`border px-3 py-1.5 text-xs ${amountMode === v ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/50"}`}>{t}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TARGETS.filter(([k]) => amountMode === "single" ? !["debit", "credit"].includes(k) : k !== "amount").map(([k, label]) => (
                <div key={k}>
                  <label className={lbl}>{label}</label>
                  <select value={mapping[k] || ""} onChange={(e) => setMapping((m) => ({ ...m, [k]: e.target.value || null }))} className={inp} data-testid={`lw-map-${k}`}>
                    <option value="">— not in file —</option>
                    {inspect.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="border border-gold-500/20 bg-navy-950 p-3">
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} className="accent-gold-500" data-testid="lw-save-template" />
                Remember this format as a template
              </label>
              {saveTemplate && <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. TechOne GL export — Clarence Valley" className={inp + " mt-2"} data-testid="lw-template-name" />}
            </div>
            {err && <div className="text-red-300 text-sm" data-testid="lw-error">{err}</div>}
            <button onClick={runImport} disabled={busy || !mapping.date || !mapping.description} data-testid="lw-import-btn"
              className="w-full bg-gold-500 text-navy-950 font-semibold py-3 hover:bg-gold-400 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />} Clean & import lines
            </button>
          </div>
        )}

        {step === 2 && result && (
          <div className="space-y-4 text-center py-4" data-testid="lw-step-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <div className="font-heading text-2xl">{result.batch.clean_rows} clean lines imported</div>
            <div className="text-white/55 text-sm font-light">
              {money(result.batch.total_amount)} total · {result.batch.excluded_rows} rows excluded (subtotals, duplicates, unparseable)
            </div>
            {result.excluded_sample.length > 0 && (
              <div className="text-left border border-gold-500/15 bg-navy-950 p-3 max-h-32 overflow-y-auto">
                <Mono className="text-[0.5rem] text-white/40 block mb-1.5">Excluded examples</Mono>
                {result.excluded_sample.slice(0, 6).map((x, i) => (
                  <div key={i} className="text-[0.65rem] text-white/45 font-light">Row {x.row}: {x.reason} — {x.description || "(blank)"}</div>
                ))}
              </div>
            )}
            <button onClick={() => onDone(result.batch.id)} data-testid="lw-done-btn"
              className="bg-gold-500 text-navy-950 font-semibold px-8 py-3 rounded-sm hover:bg-gold-400 flex items-center gap-2 mx-auto">
              <Sparkles className="h-4 w-4" /> Review & AI-classify lines
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
